import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { ArrowForwardRounded } from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useSnackbar } from "notistack";
import { authClient } from "../../lib/auth-client";
import { makeGlassFieldSx, makeGlassButtonSx, primaryButtonSx } from "./authStyles";
import { useAuthActions } from "./useAuthActions";
import { SocialAuthButtons } from "./SocialAuthButtons";
import { SIGNUP_EMAIL_NOTICE } from "./signupEmailNotice";
import { AuroraGlow } from "./AuroraGlow";

/**
 * The app's sign-in / sign-up screen, everywhere — an extension of the
 * onboarding redesign that replaced the old centered auth card. The aurora
 * field breathes across the viewport; the action column is bottom-anchored on
 * phones (where thumbs live) and vertically centered on larger screens.
 * Social is the primary path; "Continue with email" crossfades the block to
 * the form in place.
 */

const easeOut = [0.22, 1, 0.36, 1] as const;

interface AuthScreenProps {
  mode: "sign-in" | "sign-up";
}

export const AuthScreen = ({ mode }: AuthScreenProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isSignUp = mode === "sign-up";

  const { error, setError, isLoading, setIsLoading, signInWithApple, signInWithGoogle } =
    useAuthActions(mode);

  // The mode-switch link lives in the email view and lands on the other
  // route's email view (?method=email) — the remount would otherwise reset
  // the user back to the social buttons mid-flow.
  const [showEmailForm, setShowEmailForm] = useState(
    searchParams.get("method") === "email",
  );
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [isResendingVerification, setIsResendingVerification] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Success confirmations (account created, email verified, password reset)
  // are snackbars: the provider sits outside the router, so they survive the
  // GuestOnlyRoute remounts that wipe component state here.
  const { enqueueSnackbar } = useSnackbar();

  const reduced = useReducedMotion();

  // Arriving from the onboarding quiz: the account saves their tailored plan.
  const fromOnboarding = searchParams.get("from") === "onboarding";

  // Check for email verification success or session expiration on mount
  useEffect(() => {
    if (searchParams.get("email_verified") === "true") {
      // Enqueue before stripping the param — later remounts see neither, so
      // the toast can't double-fire.
      enqueueSnackbar("Email verified successfully! You can now sign in.", {
        variant: "success",
      });
      searchParams.delete("email_verified");
      setSearchParams(searchParams, { replace: true });
    }
    if (searchParams.get("reason") === "session_expired") {
      setSessionExpired(true);
      searchParams.delete("reason");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEmailSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    setShowResendVerification(false);
    setResendSuccess(false);
    setSessionExpired(false);

    if (isSignUp && password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (isSignUp && password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    setIsLoading(true);
    try {
      if (isSignUp) {
        const response = await authClient.signUp.email(
          {
            email,
            password,
            name,
            callbackURL: `${window.location.origin}/sign-in?email_verified=true`,
          },
          {
            onError: (ctx) => {
              setError(ctx.error.message ?? "Failed to create account");
              setIsLoading(false);
            },
          },
        );
        if (response?.data) {
          // Email verification is required before sign-in. The message
          // carries an instruction, so it lingers longer than the default.
          enqueueSnackbar(
            "Account created! Check your inbox for the verification link, then sign in.",
            { variant: "success", autoHideDuration: 8000 },
          );
          setIsLoading(false);
          navigate("/sign-in");
        }
        return;
      }

      const response = await authClient.signIn.email(
        { email, password },
        {
          onSuccess: () => navigate("/dashboard"),
          onError: (ctx) => {
            if (ctx.error.status === 403) {
              setError("Please verify your email address before signing in. Check your inbox for the verification link.");
              setShowResendVerification(true);
            } else {
              setError(ctx.error.message ?? "Failed to sign in");
            }
            setIsLoading(false);
          },
        },
      );
      if (response?.data?.user) {
        navigate("/dashboard");
      }
    } catch (submitError: any) {
      setError(submitError?.message ?? "Something went wrong");
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      setError("Please enter your email address to resend verification");
      return;
    }
    setIsResendingVerification(true);
    setResendSuccess(false);
    setError("");
    try {
      await authClient.sendVerificationEmail({
        email,
        callbackURL: `${window.location.origin}/sign-in?email_verified=true`,
      });
      setResendSuccess(true);
    } catch (resendError: any) {
      setError(resendError?.message ?? "Failed to resend verification email. Please try again later.");
    } finally {
      setIsResendingVerification(false);
    }
  };

  const textPrimary = isDark ? "#ffffff" : "#1d1d1f";
  const textSecondary = isDark ? "#9ca3af" : "#64748b";
  const greenLink = isDark ? "#4ade80" : "#16a34a";

  const glassFieldSx = makeGlassFieldSx(isDark);

  let headline = "Welcome back";
  let supporting = "Sign in to continue your research.";
  if (isSignUp) {
    headline = "Create your account";
    supporting = "Professional-grade stock research in your pocket.";
  }
  if (isSignUp && fromOnboarding) {
    supporting = "Two taps and you're on your way to sharper stock research.";
  }

  const switchMode = () => {
    setError("");
    const params = new URLSearchParams({ method: "email" });
    if (fromOnboarding) params.set("from", "onboarding");
    navigate(`${isSignUp ? "/sign-in" : "/sign-up"}?${params.toString()}`);
  };

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: theme.palette.background.default,
        overflow: "hidden",
      }}
    >
      <AuroraGlow />

      {/* Content column — logo rides the aurora; actions anchor to the bottom
          on phones and float centered between logo and footer on larger
          screens (the second spacer only exists from sm up). */}
      <Box
        sx={{
          position: "relative",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          px: 3,
          width: "100%",
          maxWidth: 480,
          mx: "auto",
          pt: "calc(env(safe-area-inset-top, 0px) + 28px)",
          pb: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
          // Alerts (errors, verification prompts) rise in instead of popping.
          "@keyframes authAlertIn": {
            from: { opacity: 0, transform: "translateY(-6px)" },
            to: { opacity: 1, transform: "none" },
          },
          "& .MuiAlert-root": { animation: "authAlertIn 0.3s ease" },
          "@media (prefers-reduced-motion: reduce)": {
            "& .MuiAlert-root": { animation: "none" },
          },
        }}
      >
        {/* Larger screens: matching spacer above centers the whole group. */}
        <Box sx={{ flex: { xs: 0, sm: 1 } }} />

        {/* Phones keep the logo top-left with the actions bottom-anchored;
            from sm up the logo joins the group, centered above the headline. */}
        <motion.div
          initial={reduced ? false : { opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: easeOut }}
        >
          <Box
            component="img"
            src={isDark ? "/insightLongLogoWhite.svg" : "/insightLongLogoPrimary.svg"}
            alt="Insight Analytics"
            sx={{ height: { xs: 28, sm: 32 }, width: "auto", display: "block", mx: { xs: 0, sm: "auto" } }}
          />
        </motion.div>

        <Box sx={{ flex: { xs: 1, sm: 0 }, minHeight: { sm: 40 } }} />

        <motion.div
          initial={reduced ? false : { opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: easeOut, delay: 0.08 }}
        >
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "2.25rem",
              lineHeight: 1.1,
              letterSpacing: "-0.03em",
              color: textPrimary,
              textAlign: { xs: "left", sm: "center" },
            }}
          >
            {headline}
          </Typography>
          <Typography
            sx={{
              mt: 1,
              mb: 3,
              fontSize: "1rem",
              lineHeight: 1.5,
              color: textSecondary,
              textAlign: { xs: "left", sm: "center" },
            }}
          >
            {supporting}
          </Typography>
        </motion.div>

        {sessionExpired && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            Your session has expired. Please sign in again to continue.
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}
        {resendSuccess && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
            Verification email sent! Check your inbox and click the link to verify your account.
          </Alert>
        )}
        {showResendVerification && !resendSuccess && (
          <Button
            fullWidth
            variant="outlined"
            onClick={handleResendVerification}
            disabled={isResendingVerification}
            sx={{
              mb: 2,
              py: 1.25,
              fontWeight: 600,
              textTransform: "none",
              borderRadius: 2,
              borderColor: "#22c55e",
              color: "#22c55e",
            }}
          >
            {isResendingVerification && <CircularProgress size={22} color="inherit" />}
            {!isResendingVerification && "Resend verification email"}
          </Button>
        )}

        <motion.div
          initial={reduced ? false : { opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: easeOut, delay: 0.16 }}
        >
          <AnimatePresence mode="wait" initial={false}>
            {!showEmailForm && (
              <motion.div
                key="social"
                initial={reduced ? false : { opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={reduced ? undefined : { opacity: 0, x: -16 }}
                transition={{ duration: 0.22, ease: easeOut }}
              >
                <SocialAuthButtons
                  onApple={signInWithApple}
                  onGoogle={signInWithGoogle}
                  isLoading={isLoading}
                />
                <Button
                  fullWidth
                  variant="text"
                  onClick={() => setShowEmailForm(true)}
                  disabled={isLoading}
                  sx={{
                    mt: 1.5,
                    py: 1.25,
                    fontWeight: 600,
                    fontSize: "0.9375rem",
                    textTransform: "none",
                    borderRadius: 2,
                    color: textSecondary,
                    transition: "color 0.15s ease, transform 0.1s ease",
                    "&:hover": { color: textPrimary, backgroundColor: "transparent" },
                    "&:active": { transform: "scale(0.97)" },
                  }}
                >
                  Continue with email
                </Button>
              </motion.div>
            )}

            {showEmailForm && (
              <motion.div
                key="email"
                initial={reduced ? false : { opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={reduced ? undefined : { opacity: 0, x: 16 }}
                transition={{ duration: 0.22, ease: easeOut }}
              >
                <form onSubmit={handleEmailSubmit}>
                  <Stack spacing={1.5}>
                    {isSignUp && (
                      <TextField
                        fullWidth
                        size="small"
                        label="Name"
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        required
                        disabled={isLoading}
                        autoFocus
                        sx={glassFieldSx}
                      />
                    )}
                    <TextField
                      fullWidth
                      size="small"
                      label="Email"
                      type="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      required
                      disabled={isLoading}
                      autoFocus={!isSignUp}
                      sx={glassFieldSx}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Password"
                      type="password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      required
                      disabled={isLoading}
                      helperText={
                        isSignUp && password.length > 0 && password.length < 8
                          ? "At least 8 characters"
                          : undefined
                      }
                      sx={glassFieldSx}
                    />
                    {isSignUp && (
                      <TextField
                        fullWidth
                        size="small"
                        label="Confirm password"
                        type="password"
                        value={confirmPassword}
                        onChange={(event) => setConfirmPassword(event.target.value)}
                        required
                        disabled={isLoading}
                        error={confirmPassword.length > 0 && confirmPassword !== password}
                        helperText={
                          confirmPassword.length > 0 && confirmPassword !== password
                            ? "Passwords do not match"
                            : undefined
                        }
                        sx={glassFieldSx}
                      />
                    )}
                    {!isSignUp && (
                      <Box sx={{ textAlign: "right" }}>
                        <Link
                          component="button"
                          type="button"
                          onClick={() => navigate("/forgot-password")}
                          sx={{
                            color: greenLink,
                            textDecoration: "none",
                            fontSize: "0.875rem",
                            fontWeight: 500,
                          }}
                        >
                          Forgot password?
                        </Link>
                      </Box>
                    )}
                    {/* Sign-up has no forgot-password row, so an explicit
                        spacer supplies the equivalent gap (a margin on the
                        button loses to the Stack's child-margin rule). */}
                    {isSignUp && <Box sx={{ height: 20 }} />}
                    <Button
                      fullWidth
                      type="submit"
                      disabled={isLoading}
                      sx={primaryButtonSx}
                    >
                      {isLoading && <CircularProgress size={24} color="inherit" />}
                      {!isLoading && isSignUp && "Create account"}
                      {!isLoading && !isSignUp && "Sign in"}
                    </Button>
                    {/* Secondary actions as a glass button pair, matching the
                        fields' material — the mode switch lives only in this
                        email view (the social view stays minimal; Apple/Google
                        auto-create accounts). */}
                    <Box sx={{ display: "flex", gap: 1.5, pt: 1 }}>
                      {/* Text-only: the icon pair + label can't fit half the
                          column on phones, and the social view it returns to
                          already carries the full branded buttons. */}
                      <Button
                        fullWidth
                        type="button"
                        onClick={() => setShowEmailForm(false)}
                        disabled={isLoading}
                        sx={makeGlassButtonSx(isDark)}
                      >
                        Google or Apple
                      </Button>
                      <Button
                        fullWidth
                        type="button"
                        onClick={switchMode}
                        disabled={isLoading}
                        endIcon={<ArrowForwardRounded sx={{ fontSize: 16 }} />}
                        sx={{
                          height: 44,
                          fontWeight: 600,
                          fontSize: "0.875rem",
                          textTransform: "none",
                          whiteSpace: "nowrap",
                          borderRadius: 2.5,
                          color: greenLink,
                          backgroundColor: "rgba(34, 197, 94, 0.08)",
                          backdropFilter: "blur(12px)",
                          border: "1px solid rgba(34, 197, 94, 0.2)",
                          transition:
                            "background-color 0.15s ease, border-color 0.15s ease, transform 0.1s ease",
                          "&:active": { transform: "scale(0.97)" },
                          "&:hover": {
                            backgroundColor: "rgba(34, 197, 94, 0.14)",
                            borderColor: "rgba(34, 197, 94, 0.4)",
                          },
                          "&:hover .MuiButton-endIcon": { transform: "translateX(3px)" },
                          "& .MuiButton-endIcon": { ml: 0.75, transition: "transform 0.2s ease" },
                        }}
                      >
                        {isSignUp && "Sign in instead"}
                        {!isSignUp && "Create account"}
                      </Button>
                    </Box>
                  </Stack>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Larger screens: matching spacer below centers the action column;
            terms sit past it, pinned toward the screen's bottom edge. */}
        <Box sx={{ flex: { xs: 0, sm: 1 } }} />

        <Box sx={{ mt: 3, textAlign: "center" }}>
          {isSignUp && (
            <Typography sx={{ mb: 1, color: textSecondary, fontSize: "0.75rem" }}>
              {SIGNUP_EMAIL_NOTICE}
            </Typography>
          )}
          <Link
            component="button"
            onClick={() => navigate("/terms")}
            sx={{ color: textSecondary, textDecoration: "none", fontSize: "0.75rem" }}
          >
            Terms of Service
          </Link>
          <Typography component="span" sx={{ mx: 1, color: textSecondary, fontSize: "0.75rem" }}>
            ·
          </Typography>
          <Link
            component="button"
            onClick={() => navigate("/privacy-policy")}
            sx={{ color: textSecondary, textDecoration: "none", fontSize: "0.75rem" }}
          >
            Privacy Policy
          </Link>
        </Box>
      </Box>
    </Box>
  );
};
